import { useDebouncedCallback } from '@react-hookz/web';
import type { RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';

export const useAutosizeTextArea = (
	textAreaRef: RefObject<HTMLTextAreaElement | null>,
	options: { maxGrowRows?: number } = {}
) => {
	const initialHeightRef = useRef(0);

	const callback = useDebouncedCallback(
		() => {
			const textArea = textAreaRef.current;
			if (!textArea) return;

			textArea.style.height = '0px';

			const borderWidth = parseInt(getComputedStyle(textArea).borderWidth);
			const scrollHeight = textArea.scrollHeight + borderWidth * 2;
			const lineHeight = parseInt(getComputedStyle(textArea).lineHeight);
			const maxGrowHeight = options.maxGrowRows
				? options.maxGrowRows * lineHeight + borderWidth * 2
				: Number.POSITIVE_INFINITY;

			const height = Math.max(initialHeightRef.current ?? 0, scrollHeight);
			const finalHeight = options.maxGrowRows
				? Math.min(height, maxGrowHeight)
				: height;

			textArea.style.height = `${finalHeight}px`;
			textArea.style.overflow =
				options.maxGrowRows && height > maxGrowHeight ? 'scroll' : 'hidden';
		},
		[initialHeightRef],
		200,
		200
	);

	useLayoutEffect(() => {
		const textArea = textAreaRef.current;
		if (!textArea) return;

		initialHeightRef.current = textArea.offsetHeight;
		textArea.style.overflow = 'hidden';
		textArea.style.height = `${textArea.offsetHeight}px`;

		textArea.addEventListener('beforeinput', callback);
		textArea.addEventListener('change', callback);

		return () => {
			textArea.removeEventListener('beforeinput', callback);
			textArea.removeEventListener('change', callback);
		};
	}, [textAreaRef, callback, initialHeightRef]);
};
